// src/pages/ProjectDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FaArrowLeft } from "react-icons/fa";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";
const ROLES = ["Leader", "Chairperson", "Secretary", "Treasurer"];

/* ---------- helpers ---------- */
function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
const toJsonSafe = async (res) => {
  const text = await res.text();
  try { return JSON.parse(text); } catch { throw new Error(text.slice(0, 300)); }
};
const absImg = (p) => (p?.startsWith("http") ? p : `${ABSOLUTE_HOST}${p?.startsWith("/") ? "" : "/"}${p || ""}`);

/* =================================================================== */
export default function ProjectDetail() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => readUserFromStorage(), []);
  const [projectId, setProjectId] = useState(Number(routeId) || 0);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [project, setProject] = useState(null);

  // meta
  const [description, setDescription] = useState("");
  
  const [needs, setNeeds] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // annexures
  const [annexures, setAnnexures] = useState(
    Array.from({ length: 12 }, (_, i) => ({ slot: i + 1, caption: "", url: "" }))
  );
  const [savingAnnex, setSavingAnnex] = useState(false);

  // team
  const [team, setTeam] = useState(() =>
    ROLES.reduce((acc, r) => ({ ...acc, [r]: { sliitid: "", preview: null } }), {})
  );
  const [savingTeam, setSavingTeam] = useState(false);

  // Project Officer tile
  const [officerId, setOfficerId] = useState("");
  const [officer, setOfficer] = useState(null);
  const [savingOfficer, setSavingOfficer] = useState(false);

  /* ---------- API helpers ---------- */
  const fetchProfile = useCallback(async (sliitid) => {
    if (!sliitid) return null;
    const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_profile.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ SLIITID: sliitid }),
    });
    const data = await toJsonSafe(res);
    if (data?.success && data.profile) {
      const rawImg = data.profile.image || data.profile.ProfileLink || "";
      return { name: data.profile.name || "", image: rawImg ? absImg(rawImg) : null, SLIITID: sliitid };
    }
    return null;
  }, []);

  /* ---------- load project ---------- */
  useEffect(() => {
    let cancelled = false;
    if (!projectId) { setLoading(false); setProject(null); return; }

    (async () => {
      try {
        setErr(""); setLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=get_details&id=${projectId}`);
        const data = await toJsonSafe(res);
        if (cancelled) return;

        if (!data?.success) throw new Error(data?.message || "Failed to load project");

        const p = data.project || {};
        setProject(p);
        setDescription(p.description || "");
        setNeeds(p.needs_assessment || "");

        // auto-fill officer: prefer existing officer; else creator
        const defaultOfficer = p.project_officer_sliitid || p.created_by_sliitid || "";
        setOfficerId(defaultOfficer);
        if (defaultOfficer) {
          const prof = await fetchProfile(defaultOfficer);
          if (!cancelled) setOfficer(prof);
        }

        // annexures
        const filled = Array.from({ length: 12 }, (_, i) => {
          const found = (data.annexures || []).find(a => Number(a.slot_number) === i + 1);
          return { slot: i + 1, caption: found?.caption || "", url: found?.url || "" };
        });
        setAnnexures(filled);

        // team
        const nextTeam = ROLES.reduce((acc, role) => ({ ...acc, [role]: { sliitid: "", preview: null } }), {});
        (data.team || []).forEach((t) => {
          if (ROLES.includes(t.role)) {
            nextTeam[t.role] = {
              sliitid: t.sliitid || "",
              preview: t.member ? {
                name: t.member.name || "",
                image: t.member.image ? (t.member.image.startsWith("http") ? t.member.image : absImg(t.member.image)) : null,
                SLIITID: t.sliitid || ""
              } : null
            };
          }
        });
        setTeam(nextTeam);
      } catch (e) {
        console.error(e);
        setErr("Failed to load project");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [projectId, fetchProfile]);

  /* ---------- create minimal ---------- */
  const [newName, setNewName] = useState("");
  const creating = !projectId;

  const createProject = async () => {
    const name = newName.trim();
    if (!name) return alert("Please enter a project name.");
    try {
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=create_minimal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, created_by_sliitid: user?.SLIITID || "" }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Create failed");
      setProjectId(Number(data.id));
    } catch (e) {
      console.error(e); alert("Create failed");
    }
  };

  /* ---------- saves ---------- */
  const saveMeta = async () => {
    try {
      setSavingMeta(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: Number(projectId), description, needs_assessment: needs }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) alert(data?.message || "Save failed"); else alert("Saved!");
    } catch {
      alert("Save failed");
    } finally { setSavingMeta(false); }
  };

  const saveAnnexures = async () => {
    try {
      setSavingAnnex(true);
      const payload = annexures
        .filter(a => (a.caption && a.caption.trim()) || (a.url && a.url.trim()))
        .map(a => ({ slot_number: a.slot, caption: a.caption.trim(), url: a.url.trim() }));
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=upsert_annexures`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: Number(projectId), annexures: payload }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) alert(data?.message || "Save failed"); else alert("Annexures saved!");
    } catch {
      alert("Save failed");
    } finally { setSavingAnnex(false); }
  };

  const saveTeam = async () => {
    if (!projectId) return alert("Create the project first.");
    try {
      setSavingTeam(true);
      const payload = {
        project_id: Number(projectId),
        actor_sliitid: user?.SLIITID || "",
        team: ROLES.reduce((acc, r) => ({ ...acc, [r]: team[r].sliitid || "" }), {}),
      };
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=upsert_team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Save failed");

      const next = ROLES.reduce((acc, r) => ({ ...acc, [r]: { sliitid: "", preview: null } }), {});
      (data.team || []).forEach((t) => {
        if (ROLES.includes(t.role)) {
          next[t.role] = {
            sliitid: t.sliitid || "",
            preview: t.member ? {
              name: t.member.name || "",
              image: t.member.image ? (t.member.image.startsWith("http") ? t.member.image : absImg(t.member.image)) : null,
              SLIITID: t.sliitid || ""
            } : null
          };
        }
      });
      setTeam(next);
      alert("Team saved!");
    } catch (e) {
      console.error(e); alert("Save failed");
    } finally { setSavingTeam(false); }
  };

  /* ---------- officer handlers (tile) ---------- */
  const onOfficerIdChange = async (val) => {
    const id = (val || "").toUpperCase().trim();
    setOfficerId(id);
    if (!id) { setOfficer(null); return; }
    const prof = await fetchProfile(id);
    setOfficer(prof);
  };

  const saveOfficer = async () => {
    if (!projectId) return alert("Create the project first.");
    try {
      setSavingOfficer(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_officer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: Number(projectId), officer_sliitid: officerId }),
      });
      const data = await toJsonSafe(res);
      if (!data?.success) return alert(data?.message || "Save failed");
      alert("Project Officer updated!");
    } catch (e) {
      console.error(e); alert("Save failed");
    } finally { setSavingOfficer(false); }
  };

  const exportWord = () => projectId && window.open(`${ABSOLUTE_HOST}/Backend/export_project_word.php?id=${projectId}`, "_blank");
  const exportPdf  = () => projectId && window.open(`${ABSOLUTE_HOST}/Backend/export_project_pdf.php?id=${projectId}`, "_blank");

  /* ---------- team inputs ---------- */
  const onRoleChange = (role, value) => {
    const sliitid = (value || "").toUpperCase().trim();
    setTeam((prev) => ({ ...prev, [role]: { sliitid, preview: prev[role].preview } }));
  };
  const lookupRole = async (role) => {
    const id = team[role].sliitid;
    if (!id) return;
    const p = await fetchProfile(id);
    setTeam((prev) => ({ ...prev, [role]: { sliitid: id, preview: p } }));
  };
  const clearRole = (role) => setTeam((prev) => ({ ...prev, [role]: { sliitid: "", preview: null } }));

  /* ================================ UI ================================ */
  // 👉 PHASE: append " - Phase X" after name (defaults to 1 if provided but falsy/invalid).
  const phaseNum =
    project && Number.isFinite(+project.phase_count) && +project.phase_count > 0
      ? +project.phase_count
      : (project ? 1 : null);
  const phaseLabel = phaseNum ? ` - Phase ${phaseNum}` : "";

  return (
    <div className="min-h-screen bg-[#0b132b] text-white overflow-x-hidden">
      {/* Sidebar (supports toggle in your layout) */}
      <Sidebar />

      {/* Topbar likely fixed; pad main accordingly on mobile */}
      <Topbar user={{ name: user?.name || "Member", role: user?.role || "Member", image: user?.image || "/user.png" }} />

      {/* CONTENT:
         - md+: leave room for fixed sidebar (64)
         - center content on large screens to avoid super-wide rows (max-w-screen-2xl)
      */}
      <main className="pl-0 md:pl-64 pt-16 md:pt-20 pb-10">
        <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-5 lg:px-10">
          {/* Top bar: back + status + Treasurer Report (forced CSS) */}
<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 24,
  }}
>
  {/* Left: Back */}
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <Link
      to="/club-projects"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        borderRadius: 12,
        textDecoration: "none",
        color: "#e5edff",
        background:
          "linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.05))",
        border: "1px solid rgba(255,255,255,.12)",
        boxShadow: "0 6px 18px -10px rgba(0,0,0,.6)",
        transition: "transform .15s ease, background .2s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.12)")}
      onMouseLeave={(e) =>
        (e.currentTarget.style.background =
          "linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.05))")
      }
    >
      <FaArrowLeft style={{ opacity: 0.85 }} />
      <span style={{ fontSize: 14 }}>Back to Projects</span>
    </Link>
  </div>

  {/* Right: Status + Buttons */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    }}
  >
    {project?.status ? (
      <div
        style={{
          padding: "6px 10px",
          borderRadius: 999,
          background: "rgba(148,163,184,.12)",
          border: "1px solid rgba(148,163,184,.25)",
          color: "#dbeafe",
          fontSize: 12,
        }}
      >
        Status: {project.status}
      </div>
    ) : null}

    {projectId > 0 && (
      <>
        {/* Was the mobile-only button — now styled & visible everywhere */}
        

        {/* Was the desktop-only button — keep as separate action/route */}
        <button
          onClick={() => navigate(`/project/${projectId}/tresurer`)}
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(255,255,255,.06)",
            border: "1px solid rgba(255,255,255,.12)",
            color: "#e5edff",
            cursor: "pointer",
            fontSize: 14,
            transition: "background .2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.1)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.06)")}
          title="Treasurer Report (detailed)"
        >
          Treasurer Report
        </button>
      </>
    )}
  </div>
</div>


          {/* Create-first card (forced CSS) */}
{!projectId && (
  <div
    style={{
      background:
        "linear-gradient(180deg, rgba(17,24,39,.95), rgba(17,24,39,.9))",
      border: "1px solid rgba(255,255,255,.10)",
      borderRadius: 16,
      padding: 24,
      marginBottom: 32,
      boxShadow:
        "0 18px 60px -30px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.05)",
    }}
  >
    <h2
      style={{
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: ".2px",
        margin: 0,
        marginBottom: 10,
      }}
    >
      Create Project
    </h2>

    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "stretch",
      }}
    >
      <input
        placeholder="Project name"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        style={{
          flex: "1 1 260px",
          minWidth: 0,
          background: "rgba(11,18,32,1)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 14,
          padding: "12px 14px",
          color: "#e5edff",
          outline: "none",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "rgba(99,102,241,.5)";
          e.currentTarget.style.boxShadow =
            "0 0 0 3px rgba(99,102,241,.18) inset";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,.10)";
          e.currentTarget.style.boxShadow =
            "inset 0 1px 0 rgba(255,255,255,.04)";
        }}
      />

      <button
        onClick={createProject}
        style={{
          flex: "0 0 auto",
          background:
            "linear-gradient(180deg, rgba(99,102,241,1), rgba(79,70,229,1))",
          border: "1px solid rgba(99,102,241,.55)",
          color: "white",
          padding: "12px 16px",
          borderRadius: 14,
          cursor: "pointer",
          fontWeight: 700,
          letterSpacing: ".2px",
          transition: "transform .08s ease, filter .2s ease",
          boxShadow:
            "0 10px 28px -12px rgba(79,70,229,.6), inset 0 1px 0 rgba(255,255,255,.12)",
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "translateY(1px)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1.0)")}
        title="Create project"
      >
        Create
      </button>
    </div>

    <p
      style={{
        margin: 0,
        marginTop: 8,
        fontSize: 12,
        color: "rgba(255,255,255,.55)",
      }}
    >
      We’ll auto-fill Project Officer with the creator.
    </p>
  </div>
)}


          {/* Header + Officer tile (stack on mobile, 2-col on lg) */}
          {projectId > 0 && (
  <>
    {/* local, component-scoped CSS for responsive grid & polish */}
    <style>{`
      .pd-grid {
        display: grid;
        gap: 20px;
        margin-bottom: 32px;
      }
      @media (min-width: 1024px) {
        .pd-grid { grid-template-columns: 2fr 1fr; gap: 24px; }
      }

      .pd-card {
        background: radial-gradient(1000px 420px at 5% -60%, rgba(59,130,246,.18), transparent 60%),
                    linear-gradient(180deg, rgba(17,24,39,.94), rgba(17,24,39,.88));
        border: 1px solid rgba(147,197,253,.16);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 22px 60px -30px rgba(0,0,0,.65), inset 0 1px 0 rgba(147,197,253,.08);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      }

      .pd-title {
        margin: 0;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: .2px;
        line-height: 1.25;
        word-break: break-word;
        background: linear-gradient(180deg, #e5edff 0%, #c7d2fe 60%, #93c5fd 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .pd-meta {
        margin-top: 6px;
        font-size: 13px;
        color: rgba(255,255,255,.70);
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
      }

      .pd-chip {
        background: rgba(147,197,253,.10);
        border: 1px solid rgba(147,197,253,.22);
        padding: 3px 8px;
        border-radius: 999px;
      }

      .pd-chip--ok {
        background: rgba(16,185,129,.12);
        border: 1px solid rgba(16,185,129,.28);
        color: #bbf7d0;
      }

      .pd-card-officer {
        background: linear-gradient(180deg, rgba(17,24,39,.95), rgba(17,24,39,.9));
        border: 1px solid rgba(255,255,255,.10);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 16px 44px -28px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.05);
      }

      .pd-field {
        flex: 1 1 auto;
        min-width: 0;
        background: rgba(11,18,32,1);
        border: 1px solid rgba(255,255,255,.10);
        border-radius: 14px;
        padding: 10px 12px;
        color: #e5edff;
        outline: none;
        box-shadow: inset 0 1px 0 rgba(255,255,255,.04);
        transition: border-color .15s ease, box-shadow .15s ease;
      }
      .pd-field:focus {
        border-color: rgba(99,102,241,.5);
        box-shadow: 0 0 0 3px rgba(99,102,241,.18) inset;
      }

      .pd-btn {
        padding: 10px 12px;
        border-radius: 12px;
        cursor: pointer;
        font-weight: 700;
        letter-spacing: .2px;
        transition: transform .08s ease, filter .2s ease, opacity .2s ease;
        color: #e5edff;
        border: 1px solid rgba(255,255,255,.10);
        background: rgba(255,255,255,.08);
      }
      .pd-btn:hover { filter: brightness(1.05); }
      .pd-btn:active { transform: translateY(1px); }
      .pd-btn[disabled] { opacity: .6; cursor: not-allowed; }

      .pd-error {
        color: #fecaca;
        background: rgba(239,68,68,.10);
        border: 1px solid rgba(239,68,68,.25);
        padding: 10px 12px;
        border-radius: 12px;
        font-size: 14px;
      }

      .pd-skeleton-1,
      .pd-skeleton-2 {
        border-radius: 8px;
        animation: pdPulse 1.4s ease-in-out infinite;
      }
      .pd-skeleton-1 { height: 14px; width: 180px; background: rgba(255,255,255,.12); margin-bottom: 10px; }
      .pd-skeleton-2 { height: 10px; width: 260px; background: rgba(255,255,255,.08); }

      @keyframes pdPulse { 0%,100% { opacity:.55 } 50% { opacity:1 } }
    `}</style>

    <div className="pd-grid">
      {/* Header */}
      <div className="pd-card">
        {loading ? (
          <div>
            <div className="pd-skeleton-1" />
            <div className="pd-skeleton-2" />
          </div>
        ) : err ? (
          <div className="pd-error">{err}</div>
        ) : (
          <>
            {/* 👇 Project name + Phase */}
            <h1 className="pd-title">
              {(project?.name || "Project")}{phaseLabel}
            </h1>

            {/* Meta */}
            <div className="pd-meta">
              <span className="pd-chip">
                Category: {project?.category_name || "—"}
              </span>
              {project?.approved_by ? (
                <span className="pd-chip pd-chip--ok">
                  Approved by: {project.approved_by}
                  {project.approved_at ? ` on ${project.approved_at.split(" ")[0]}` : ""}
                </span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* Project Officer tile */}
      <div className="pd-card-officer">
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.60)" }}>Project Officer</div>

        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={officer?.image || "/user.png"}
            onError={(e)=>{ e.currentTarget.src="/user.png"; }}
            alt="officer"
            style={{ height: 48, width: 48, borderRadius: "999px", objectFit: "cover", background: "#0b1220" }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              title={officer?.name || "—"}
              style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {officer?.name || "—"}
            </div>
            <div
              title={officerId || ""}
              style={{ fontSize: 12, color: "rgba(255,255,255,.55)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {officerId || ""}
            </div>
          </div>
        </div>

        {/* Compact input to change officer by SLIIT ID */}
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={officerId}
            onChange={(e)=>onOfficerIdChange(e.target.value)}
            placeholder="SLIIT ID (auto shows name)"
            className="pd-field"
          />
          <button
            onClick={saveOfficer}
            disabled={savingOfficer}
            className="pd-btn"
            title="Save officer"
          >
            {savingOfficer ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  </>
)}


          {/* Main content (stack on mobile; 2 cols on lg) */}
          {projectId > 0 && (
            <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
              {/* Description */}
<section
  style={{
    background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 12px 40px -15px rgba(0,0,0,0.7)"
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "12px"
    }}
  >
    <span
      style={{
        display: "inline-block",
        width: "6px",
        height: "20px",
        borderRadius: "6px",
        backgroundColor: "#6366f1" // indigo
      }}
    />
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        margin: 0
      }}
    >
      Project Description
    </h2>
  </div>

  <textarea
    rows={6}
    value={description}
    onChange={(e) => setDescription(e.target.value)}
    placeholder="Describe your project..."
    style={{
      width: "100%",
      backgroundColor: "rgba(11,18,32,0.9)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "12px",
      padding: "16px",
      fontSize: "14px",
      color: "rgba(255,255,255,0.85)",
      resize: "vertical",
      outline: "none",
      transition: "all 0.2s ease",
      marginBottom: "20px",
      boxSizing: "border-box"
    }}
    onFocus={(e) => {
      e.target.style.border = "1px solid #6366f1";
      e.target.style.boxShadow = "0 0 0 2px rgba(99,102,241,0.4)";
    }}
    onBlur={(e) => {
      e.target.style.border = "1px solid rgba(255,255,255,0.1)";
      e.target.style.boxShadow = "none";
    }}
  />

  <div
    style={{
      marginTop: "20px",
      display: "flex",
      flexWrap: "wrap",
      gap: "12px"
    }}
  >
    {/* Save Button */}
    <button
      onClick={saveMeta}
      disabled={savingMeta}
      style={{
        padding: "10px 20px",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 500,
        backgroundColor: "#4f46e5", // indigo-600
        color: "white",
        cursor: savingMeta ? "not-allowed" : "pointer",
        opacity: savingMeta ? 0.6 : 1,
        transition: "all 0.2s ease",
        boxShadow: "0 8px 24px -8px rgba(99,102,241,0.55)"
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "#4338ca"; // darker indigo
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "#4f46e5";
      }}
    >
      {savingMeta ? "Saving…" : "Save"}
    </button>

    {/* Word Download */}
    <button
      onClick={exportWord}
      style={{
        padding: "10px 20px",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 500,
        backgroundColor: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.1)",
        color: "white",
        transition: "all 0.2s ease",
        cursor: "pointer"
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
      }}
    >
      Download Word
    </button>

    {/* PDF Download */}
    <button
      onClick={exportPdf}
      style={{
        padding: "10px 20px",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 500,
        backgroundColor: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.1)",
        color: "white",
        transition: "all 0.2s ease",
        cursor: "pointer"
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
      }}
    >
      Download PDF
    </button>
  </div>
</section>



              {/* Needs Assessment */}
<section
  style={{
    background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #2a2f3f",
    boxShadow: "0 12px 40px -15px rgba(0,0,0,0.7)"
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "12px"
    }}
  >
    <span
      style={{
        display: "inline-block",
        width: "6px",
        height: "20px",
        borderRadius: "6px",
        backgroundColor: "#6366f1" // indigo-500
      }}
    />
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        margin: 0
      }}
    >
      Needs Assessment
    </h2>
  </div>

  <textarea
    rows={6}
    value={needs}
    onChange={(e) => setNeeds(e.target.value)}
    placeholder="What is the need / problem this project addresses?"
    style={{
      width: "100%",
      backgroundColor: "rgba(11,18,32,0.9)",
      border: "1px solid #2a2f3f",
      borderRadius: "12px",
      padding: "14px",
      fontSize: "14px",
      color: "rgba(255,255,255,0.85)",
      resize: "vertical",
      outline: "none",
      transition: "all 0.2s ease",
      boxSizing: "border-box"
    }}
    onFocus={(e) => {
      e.target.style.border = "1px solid #6366f1";
      e.target.style.boxShadow = "0 0 0 2px rgba(99,102,241,0.4)";
    }}
    onBlur={(e) => {
      e.target.style.border = "1px solid #2a2f3f";
      e.target.style.boxShadow = "none";
    }}
  />
</section>



              {/* Team */}
<section
  style={{
    background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #2a2f3f",
    boxShadow: "0 12px 40px -15px rgba(0,0,0,0.65)",
    gridColumn: "span 2"
  }}
>
  <h2
    style={{
      fontWeight: 600,
      marginBottom: "12px",
      fontSize: "16px",
      color: "rgba(255,255,255,0.9)"
    }}
  >
    Project Team
  </h2>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "12px"
    }}
  >
    {ROLES.map((role) => {
      const entry = team[role];
      return (
        <div
          key={role}
          style={{
            backgroundColor: "rgba(11,18,32,0.95)",
            border: "1px solid #2a2f3f",
            borderRadius: "12px",
            padding: "12px"
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "rgba(255,255,255,0.6)",
              marginBottom: "6px"
            }}
          >
            {role}
          </div>

          <div style={{ display: "flex", gap: "6px" }}>
            <input
              placeholder="SLIIT ID (e.g. IT21808784)"
              value={entry.sliitid}
              onChange={(e) => onRoleChange(role, e.target.value)}
              style={{
                flex: 1,
                backgroundColor: "#0b1220",
                border: "1px solid #2a2f3f",
                borderRadius: "8px",
                padding: "6px 8px",
                fontSize: "13px",
                color: "white",
                outline: "none"
              }}
            />
            <button
              onClick={() => lookupRole(role)}
              title="Lookup"
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid #2a2f3f",
                padding: "6px 8px",
                borderRadius: "6px",
                fontSize: "12px",
                color: "white",
                cursor: "pointer"
              }}
            >
              Lookup
            </button>
            <button
              onClick={() => clearRole(role)}
              title="Clear"
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid #2a2f3f",
                padding: "6px 8px",
                borderRadius: "6px",
                fontSize: "12px",
                color: "white",
                cursor: "pointer"
              }}
            >
              Clear
            </button>
          </div>

          {entry.preview && (
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              <img
                src={entry.preview.image || "/user.png"}
                alt="member"
                onError={(e) => (e.currentTarget.src = "/user.png")}
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "50%",
                  objectFit: "cover"
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "white",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {entry.preview.name || entry.preview.SLIITID}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.6)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {entry.preview.SLIITID}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>

  <div style={{ marginTop: "14px" }}>
    <button
      onClick={saveTeam}
      disabled={savingTeam}
      style={{
        backgroundColor: savingTeam ? "#4b5563" : "#4f46e5",
        color: "white",
        padding: "8px 16px",
        borderRadius: "10px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: savingTeam ? "not-allowed" : "pointer",
        opacity: savingTeam ? 0.6 : 1,
        transition: "background 0.3s"
      }}
    >
      {savingTeam ? "Saving…" : "Save Team"}
    </button>
  </div>
</section>

              {/* Annexures */}
<section
  style={{
    background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
    borderRadius: "16px",
    padding: "20px",
    border: "1px solid #2a2f3f",
    boxShadow: "0 12px 40px -15px rgba(0,0,0,0.65)",
    gridColumn: "span 2" // replaces lg:col-span-2
  }}
>
  <h2
    style={{
      fontWeight: 600,
      marginBottom: "12px",
      fontSize: "16px",
      color: "rgba(255,255,255,0.9)"
    }}
  >
    Annexures (up to 12)
  </h2>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "12px"
    }}
  >
    {annexures.map((a, idx) => (
      <div
        key={a.slot}
        style={{
          backgroundColor: "rgba(11,18,32,0.95)",
          border: "1px solid #2a2f3f",
          borderRadius: "12px",
          padding: "12px"
        }}
      >
        <div
          style={{
            fontSize: "11px",
            color: "rgba(255,255,255,0.6)",
            marginBottom: "8px"
          }}
        >
          Slot {a.slot}
        </div>

        <input
          placeholder="Caption"
          value={a.caption}
          onChange={(e) => {
            const n = [...annexures];
            n[idx] = { ...n[idx], caption: e.target.value };
            setAnnexures(n);
          }}
          style={{
            width: "100%",
            marginBottom: "8px",
            backgroundColor: "#0b1220",
            border: "1px solid #2a2f3f",
            borderRadius: "8px",
            padding: "8px 10px",
            fontSize: "13px",
            color: "white",
            outline: "none"
          }}
        />

        <input
          placeholder="https://link"
          value={a.url}
          onChange={(e) => {
            const n = [...annexures];
            n[idx] = { ...n[idx], url: e.target.value };
            setAnnexures(n);
          }}
          style={{
            width: "100%",
            backgroundColor: "#0b1220",
            border: "1px solid #2a2f3f",
            borderRadius: "8px",
            padding: "8px 10px",
            fontSize: "13px",
            color: "white",
            outline: "none"
          }}
        />
      </div>
    ))}
  </div>

  <div style={{ marginTop: "14px" }}>
    <button
      onClick={saveAnnexures}
      disabled={savingAnnex}
      style={{
        backgroundColor: savingAnnex ? "#4b5563" : "#4f46e5",
        color: "white",
        padding: "8px 16px",
        borderRadius: "10px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: savingAnnex ? "not-allowed" : "pointer",
        opacity: savingAnnex ? 0.6 : 1,
        transition: "background 0.3s"
      }}
    >
      {savingAnnex ? "Saving…" : "Save Annexures"}
    </button>
  </div>
</section>

            </div>
          )}
        </div>
      </main>
    </div>
  );
}
